import { useT, useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    intro:
      "Hinter jedem veredelten Bauteil steht ein Team mit Erfahrung, persönlicher Verantwortung und einer klaren Haltung. Sie erreichen uns direkt — ohne Umwege.",
    members: [
      { name: "Marco Mandic", role: "Geschäftsführer", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marco-Mandic_klein-300x240.jpg" },
      { name: "Marianne Guntli", role: "Administration", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marianne-Guntli_klein-300x240.jpg" },
      { name: "Rustem Sabedini", role: "Werkstatt · Unterhalt", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Rustem-Sabedini_klein-300x240.jpg" },
    ],
  },
  en: {
    intro:
      "Behind every finished part stands a team with experience, personal accountability and a clear stance. You can reach us directly — no detours.",
    members: [
      { name: "Marco Mandic", role: "Managing Director", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marco-Mandic_klein-300x240.jpg" },
      { name: "Marianne Guntli", role: "Administration", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marianne-Guntli_klein-300x240.jpg" },
      { name: "Rustem Sabedini", role: "Workshop · Maintenance", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Rustem-Sabedini_klein-300x240.jpg" },
    ],
  },
  fr: {
    intro:
      "Derrière chaque pièce traitée se trouve une équipe expérimentée, qui assume sa responsabilité et défend une vision claire. Vous nous joignez directement — sans détour.",
    members: [
      { name: "Marco Mandic", role: "Directeur général", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marco-Mandic_klein-300x240.jpg" },
      { name: "Marianne Guntli", role: "Administration", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marianne-Guntli_klein-300x240.jpg" },
      { name: "Rustem Sabedini", role: "Atelier · Maintenance", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Rustem-Sabedini_klein-300x240.jpg" },
    ],
  },
  it: {
    intro:
      "Dietro ogni componente trattato c'è un team con esperienza, responsabilità personale e un atteggiamento chiaro. Ci raggiungete direttamente — senza intermediari.",
    members: [
      { name: "Marco Mandic", role: "Direttore generale", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marco-Mandic_klein-300x240.jpg" },
      { name: "Marianne Guntli", role: "Amministrazione", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Marianne-Guntli_klein-300x240.jpg" },
      { name: "Rustem Sabedini", role: "Officina · Manutenzione", image: "https://fritzkammag.ch/wp-content/uploads/2020/08/Fritz-Kamm-AG-2020-06-08-Rustem-Sabedini_klein-300x240.jpg" },
    ],
  },
};

export const Team = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-[#0E0E10] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.team_overline", "— Team")} · 06 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.team_title_1", "Menschen,")}
              <br />
              <span className="text-zinc-500">{t("sections.team_title_2", "die für Qualität stehen.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {c.members.map((m, i) => (
            <article
              key={m.name}
              data-testid={`team-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`reveal reveal-delay-${i + 1} group relative bg-[#0E0E10] overflow-hidden`}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={m.image}
                  alt={m.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E10] via-transparent to-transparent" />
              </div>
              <div className="p-6 md:p-8 border-t border-white/10">
                <div className="overline mb-2">0{i + 1}</div>
                <h3 className="font-display text-2xl md:text-[28px] font-semibold tracking-tight text-white mb-1">
                  {m.name}
                </h3>
                <div className="text-zinc-400 text-sm">{m.role}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
